import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Icon, Spinner, Text, Button } from "~/components-shared";
import Picture from "~/components-shared/Picture";
import api from "~/lib/api";
import { useTranslation } from "react-i18next";
import { AIGenerateTargetEnum } from "~/types/enums";
import AiGenerateButton from "~/components/AiGenerateButton";
import { dataURLtoFile } from "~/lib/helpers";
import { genericColorCombination } from "~/lib/resolvers";
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: ${_ref => {
  let {
    $width
  } = _ref;
  return $width;
}};
  min-width: ${_ref2 => {
  let {
    $width
  } = _ref2;
  return $width;
}};
  max-width: ${_ref3 => {
  let {
    $width
  } = _ref3;
  return $width;
}};
  ${_ref4 => {
  let {
    $s,
    theme
  } = _ref4;
  return theme.spacing.resolve(theme, $s);
}};
`;
const Input = styled.input`
  cursor: pointer;
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  top: 0;
  left: 0;
  transform: scale(4);
`;
const Area = styled.div`
  overflow: hidden;
  background-color: ${_ref5 => {
  let {
    $hasImage,
    theme
  } = _ref5;
  return $hasImage ? "transparent" : genericColorCombination(theme, "translucentPrimary").background;
}};
  color: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return genericColorCombination(theme, "translucentPrimary").color;
}};
  font-weight: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return theme.typography.fontWeight.regular;
}};
  width: 100%;
  padding-top: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  position: relative;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  ${_ref8 => {
  let {
    $shiftBottom
  } = _ref8;
  return $shiftBottom && "margin-bottom: 1rem";
}};
  ${_ref9 => {
  let {
    $outline,
    theme
  } = _ref9;
  return theme.shadows.outline(theme, $outline);
}};
  ${_ref10 => {
  let {
    $border,
    theme
  } = _ref10;
  switch ($border) {
    case "rounded":
      return `border-radius: ${theme.shape.borderRadius.primary}`;
    case "circled":
      return "border-radius: 50%";
    default:
      return null;
  }
}};

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: background-color 200ms ease-out;
    background-color: transparent;
    ${_ref11 => {
  let {
    $border,
    theme
  } = _ref11;
  switch ($border) {
    case "rounded":
      return `border-radius: ${theme.shape.borderRadius.primary}`;
    case "circled":
      return "border-radius: 50%";
    default:
      return null;
  }
}};
    z-index: -1;
  }

  &:hover:before {
    background-color: ${_ref12 => {
  let {
    theme
  } = _ref12;
  return theme.colors.action.hover;
}};
  }
`;
const Label = styled.div`
  width: 80%;
  position: absolute;
  top: ${_ref13 => {
  let {
    $shiftTop
  } = _ref13;
  return $shiftTop ? "45%" : "55%";
}};
  transform: translateY(-50px);
`;
const SpinnerWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  background-color: ${_ref14 => {
  let {
    theme
  } = _ref14;
  return theme.colors.primary.dark;
}};
  width: 100%;
  height: 100%;
  z-index: 1;

  & > * {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;
const StyledPicture = styled(Picture)`
  position: absolute;
  top: 0;
  left: 0;
`;
const StyledButton = styled(Button)`
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: ${_ref15 => {
  let {
    theme
  } = _ref15;
  return theme.colors.background.primary;
}} 0 0 0 2px;
`;
const Content = styled.div`
  display: block;
  position: relative;
  height: ${_ref16 => {
  let {
    $height
  } = _ref16;
  return $height;
}};
  ${_ref17 => {
  let {
    $shiftBottom
  } = _ref17;
  return $shiftBottom && "margin-bottom: 1.2rem";
}};
`;
const ImageAiButton = styled(AiGenerateButton)`
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: rgb(255 255 255) 0 0 0 2px;
`;
const ErrorMessage = styled.span`
  font-size: 0.8em;
  display: inline-block;
  margin-left: 0.5em;
  color: ${_ref18 => {
  let {
    theme
  } = _ref18;
  return theme.colors.danger.main;
}};
`;
const ImageUpload = _ref19 => {
  let {
    initPreview,
    label,
    s,
    outline,
    category,
    width = "120px",
    height = "120px",
    border = "rounded",
    onChange = () => false,
    value,
    ai,
    ...rest
  } = _ref19;
  const [preview, setPreview] = useState(initPreview);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(undefined);
  const {
    t
  } = useTranslation();
  useEffect(() => {
    if (initPreview) {
      setPreview(initPreview);
    }
  }, [initPreview]);
  const validateImage = file => {
    if (!file.type.startsWith("image/")) {
      throw new Error(t("ImageUploadValidation.type"));
    }

    // Max file size 5mb
    if (file.size > 5000000) {
      throw new Error(t("ImageUploadValidation.oversize"));
    }
  };
  const uploadImage = async e => {
    if (e.target.files.length) {
      try {
        setError(undefined);
        setLoading(true);
        const file = e.target.files[0];
        validateImage(file);
        const uploadedImage = await api.uploadImage(file, category);
        setPreview(uploadedImage.url);
        onChange(uploadedImage.key);
      } catch (err) {
        setError(err.toString());
      } finally {
        setLoading(false);
      }
    }
  };
  const uploadAiImage = async base64 => {
    if (base64) {
      try {
        setError(undefined);
        setLoading(true);
        const file = dataURLtoFile(`data:image/jpeg;base64,${base64}`, "ai_generated");
        const uploadedImage = await api.uploadImage(file, category);
        setPreview(uploadedImage.url);
        onChange(uploadedImage.key);
      } catch (err) {
        setError(err.toString());
      } finally {
        setLoading(false);
      }
    }
  };
  const removeImage = () => {
    onChange("");
    setPreview(undefined);
  };
  return /*#__PURE__*/React.createElement(Wrapper, _extends({
    $s: s,
    $width: width
  }, rest), /*#__PURE__*/React.createElement(Content, {
    $height: height,
    $shiftBottom: !!ai
  }, /*#__PURE__*/React.createElement(Area, {
    $hasImage: !!preview,
    $outline: outline,
    $border: border,
    $shiftBottom: !!ai || !!preview
  }, loading && /*#__PURE__*/React.createElement(SpinnerWrapper, null, /*#__PURE__*/React.createElement(Spinner, null)), preview ? /*#__PURE__*/React.createElement(StyledPicture, {
    src: preview,
    border: border,
    height: "100%",
    width: "100%"
  }) : /*#__PURE__*/React.createElement(Label, {
    $shiftTop: !!ai
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "photo",
    scale: 10,
    color: "inherit",
    s: {
      mb: 1
    }
  }), label && /*#__PURE__*/React.createElement(Text, {
    display: "block"
  }, label), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    fontSize: 3,
    s: {
      mb: 3
    }
  }, t("ImageUploadValidation.maxSize"))), /*#__PURE__*/React.createElement(Input, {
    type: "file",
    id: "uploadedImage",
    accept: "image/*",
    onChange: uploadImage
  })), !!ai && !preview && /*#__PURE__*/React.createElement(ImageAiButton, {
    target: AIGenerateTargetEnum.COMMUNITY_IMAGE,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": ai.tip,
    value: ai.value,
    noValueError: ai.noValueError,
    onGenerateStarted: () => setError(undefined),
    onGenerateSucceeded: file => uploadAiImage(file[0]?.b64_json),
    onGenerateFailed: message => setError(message)
  }), preview && /*#__PURE__*/React.createElement(StyledButton, {
    onClick: removeImage,
    color: "dangerGrey",
    fontSize: 3,
    s: {
      px: 2,
      py: 0
    },
    rounded: true
  }, t("common.actions.remove"))), error && /*#__PURE__*/React.createElement(ErrorMessage, null, error));
};
export default ImageUpload;